import React, { useRef, useState, useEffect } from "react";
import {
  GoogleMap,
  MarkerF,
  DrawingManager,
  PolygonF,
  useJsApiLoader,
} from "@react-google-maps/api";
import apiUrl from "../api";

// Example properties array. Replace with your real data or pass as props.
const exampleProperties = [
  { lat: 33.4219, lng: 28.2143, name: "Property 1" },
  { lat: 33.425, lng: 28.22, name: "Property 2" },
  { lat: 33.43, lng: 28.21, name: "Property 3" },
];

const libraries = ["places", "drawing", "geometry"];

const MapWithPolygons = ({ properties = [] }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCNcgVQHh0h_A0nfgPtoMsFe0DCiilTKIU", // Replace with your actual API key
    libraries,
  });

  const mapRef = useRef();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocationName, setNewLocationName] = useState("");
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });

  // Polygon ref for editing
  const editingPolygonRef = useRef();
  // Listener refs for cleanup
  const pathListenersRef = useRef([]);

  // Fetch all locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);
  const user = JSON.parse(localStorage.getItem("user"));
  console.log("Fetching locations for user:", user);

  const token = user?.token;
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/location/getLocations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
      showNotification("Failed to fetch locations", "error");
    } finally {
      setLoading(false);
    }
  };

  const saveLocation = async (polygonPath, name, locationId = null) => {
    try {
      const locationData = {
        name: name,
        polygon: polygonPath,
        color: "#FF0000", // Default color
        description: "",
      };
      let response;
      if (locationId) {
        response = await fetch(
          `${apiUrl}/location/updateLocation/${locationId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(locationData),
          }
        );
      } else {
        response = await fetch(`${apiUrl}/location/createLocation`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(locationData),
        });
      }
      if (response.ok) {
        await fetchLocations();
        setNewLocationName("");
        setIsEditing(false);
        setEditingLocation(null);
        setCurrentPolygon(null);
        showNotification(
          locationId
            ? "Location updated successfully!"
            : "Location saved successfully!",
          "success"
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save location");
      }
    } catch (error) {
      console.error("Error saving location:", error);
      showNotification("Failed to save location", "error");
    }
  };

  const deleteLocation = async (locationId) => {
    if (!window.confirm("Are you sure you want to delete this location?")) {
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/location/deleteLocation/${locationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        await fetchLocations();
        setSelectedLocation(null);
        showNotification("Location deleted successfully!", "success");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete location");
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      showNotification("Failed to delete location", "error");
    }
  };

  const onPolygonComplete = async (polygon) => {
    const google = window.google;

    // Get polygon path
    const path = polygon
      .getPath()
      .getArray()
      .map((latlng) => ({
        lat: latlng.lat(),
        lng: latlng.lng(),
      }));

    // Check which properties are inside
    const { poly } = await google.maps.importLibrary("geometry");
    const inside = properties.filter((property) =>
      poly.containsLocation(
        new google.maps.LatLng(property.lat, property.lng),
        polygon
      )
    );
    console.log("Properties inside polygon:", inside);

    // Store the polygon for saving
    setCurrentPolygon({ polygon, path });

    // Prompt for location name if not editing
    if (!isEditing) {
      const name = prompt("Enter a name for this location:");
      if (name && name.trim()) {
        await saveLocation(path, name.trim());
      }
    } else if (editingLocation) {
      // Update existing location
      const name = newLocationName || editingLocation.name;
      await saveLocation(path, name, editingLocation._id);
    }

    // Remove the drawn polygon from map (it will be replaced by our stored polygon)
    polygon.setMap(null);
  };

  const showLocationOnMap = (location) => {
    setSelectedLocation(location);

    // Center map on the polygon
    if (location.polygon && location.polygon.length > 0 && mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      location.polygon.forEach((point) => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const startEditing = (location) => {
    setIsEditing(true);
    setEditingLocation(location);
    setNewLocationName(location.name);
    setCurrentPolygon({ path: location.polygon }); // Initialize with current path
    showLocationOnMap(location);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingLocation(null);
    setNewLocationName("");
    setCurrentPolygon(null);
  };

  // Attach listeners to polygon path when editing
  useEffect(() => {
    if (isEditing && editingPolygonRef.current) {
      const polygon = editingPolygonRef.current;
      const path = polygon.getPath();
      const updatePath = () => {
        const newPath = path.getArray().map((latlng) => ({
          lat: latlng.lat(),
          lng: latlng.lng(),
        }));
        setCurrentPolygon({ path: newPath });
      };
      // Listen for path changes
      pathListenersRef.current = [
        window.google.maps.event.addListener(path, "set_at", updatePath),
        window.google.maps.event.addListener(path, "insert_at", updatePath),
        window.google.maps.event.addListener(path, "remove_at", updatePath),
      ];
      // Set initial path
      updatePath();
      return () => {
        // Remove listeners
        pathListenersRef.current.forEach((listener) =>
          window.google.maps.event.removeListener(listener)
        );
        pathListenersRef.current = [];
      };
    }
  }, [isEditing, editingLocation]);

  // Save edited polygon
  // 1️⃣  Replace the whole body of saveEditedPolygon with this
// ⬇⬇ Replace *everything* inside saveEditedPolygon with this
const saveEditedPolygon = async () => {
  // nothing to save if we don’t have the live polygon or a location
  console.log("Saving edited polygon", {
    polygon: editingPolygonRef.current,
    location: editingLocation,
    newName: newLocationName,
  });
  if (!editingPolygonRef.current || !editingLocation) return;

  // 1. Read the *actual* vertices that are on the screen right now
  const polygonPath = editingPolygonRef.current
    .getPath()           // MVCArray<LatLng>
    .getArray()          // LatLng[]
    .map((latLng) => ({
      lat: latLng.lat(),
      lng: latLng.lng(),
    }));

  // 2. Keep whatever name the user typed (or fall back to the original)
  const name = (newLocationName || editingLocation.name).trim();

  // 3. Persist
  await saveLocation(polygonPath, name, editingLocation._id);
};

  // Helper to show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 3500);
  };

  // Ensure map navigates to selected polygon after render
  useEffect(() => {
    if (!selectedLocation || !selectedLocation.polygon || !mapRef.current) return;
    // Wait for next tick to ensure polygon is rendered
    setTimeout(() => {
      const bounds = new window.google.maps.LatLngBounds();
      selectedLocation.polygon.forEach((point) => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
      mapRef.current.fitBounds(bounds);
    }, 0);
  }, [selectedLocation, locations, isEditing]);

  return isLoaded ? (
    <div style={{ display: "flex", height: "600px" }}>
      {/* Notification */}
      {notification.message && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: notification.type === "error" ? "#dc3545" : "#28a745",
            color: "white",
            padding: "14px 24px",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}
        >
          <span>{notification.message}</span>
          <button
            onClick={() => setNotification({ message: '', type: '' })}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "18px",
              marginLeft: "10px",
              cursor: "pointer"
            }}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      )}

      {/* Sidebar with locations list */}
      <div
        style={{
          width: "300px",
          padding: "20px",
          borderRight: "1px solid #ccc",
          overflowY: "auto",
          backgroundColor: "#f9f9f9",
        }}
      >
        <h3>Saved Locations</h3>

        {isEditing && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#e8f4fd",
              borderRadius: "5px",
              marginBottom: "10px",
            }}
          >
            <h4>Editing: {editingLocation?.name}</h4>
            <input
              type="text"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              placeholder="Location name"
              style={{
                width: "100%",
                padding: "5px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "3px",
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={cancelEditing}
                style={{
                  padding: "5px 10px",
                  marginRight: "10px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveEditedPolygon}
                style={{
                  padding: "5px 10px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: currentPolygon && currentPolygon.path ? "pointer" : "not-allowed",
                  opacity: currentPolygon && currentPolygon.path ? 1 : 0.6,
                }}
              //  disabled={!editingPolygonRef.current}
              >
                Save 
              </button>
              <small>Draw a new polygon to update</small>
            </div>
          </div>
        )}

        {loading ? (
          <p>Loading locations...</p>
        ) : (
          <div>
            {locations.length === 0 ? (
              <p>
                No locations saved yet. Draw a polygon on the map to create one.
              </p>
            ) : (
              locations.map((location) => (
                <div
                  key={location._id}
                  style={{
                    padding: "10px",
                    margin: "5px 0",
                    backgroundColor:
                      selectedLocation?._id === location._id
                        ? "#007bff"
                        : "white",
                    color:
                      selectedLocation?._id === location._id
                        ? "white"
                        : "black",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                  onClick={() => showLocationOnMap(location)}
                >
                  <div >
                    <strong>{location.name}</strong>
                    {location.description && (
                      <p
                        style={{
                          margin: "5px 0",
                          fontSize: "12px",
                          opacity: 0.8,
                        }}
                      >
                        {location.description}
                      </p>
                    )}
                    <small>
                      Created:{" "}
                      {new Date(location.created_at).toLocaleDateString()}
                    </small>
                  </div>
                  <div style={{ marginTop: "10px" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(location);
                      }}
                      style={{
                        padding: "3px 8px",
                        marginRight: "5px",
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteLocation(location._id);
                      }}
                      style={{
                        padding: "3px 8px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {!isEditing && (
          <div
            style={{
              marginTop: "20px",
              padding: "10px",
              backgroundColor: "#fff3cd",
              borderRadius: "5px",
            }}
          >
            <small>
              <strong>Instructions:</strong>
              <br />
              • Click a location to view it on the map
              <br />
              • Use the polygon tool to draw a new location
              <br />• Edit or delete existing locations using the buttons
            </small>
          </div>
        )}
      </div>

      {/* Map container */}
      <div style={{ flex: 1 }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={{ lat: 36.202409267558814,  lng: 27.95057909006215}}
          zoom={9}
          onLoad={(map) => (mapRef.current = map)}
        >
          {/* Property markers */}
          {properties.map((p, i) => (
            <MarkerF key={i} position={{ lat: p.lat, lng: p.lng }} />
          ))}

          {/* Saved location polygons */}
          {locations.map((location) => {
            if (isEditing && editingLocation && editingLocation._id === location._id) {
              // Render polygon for the one being edited, but not editable
              return (
                <PolygonF
                  key={location._id}
                  path={currentPolygon?.path || location.polygon}
                  options={{
                    editable: false,
                    draggable: false,
                    fillColor: location.color || "#FF0000",
                    fillOpacity: 0.4,
                    strokeColor: location.color || "#FF0000",
                    strokeOpacity: 1,
                    strokeWeight: 3,
                  }}
                />
              );
            }
            // Render normal polygons for others
            return (
              <PolygonF
                key={location._id}
                path={location.polygon}
                options={{
                  fillColor: location.color || "#FF0000",
                  fillOpacity: selectedLocation?._id === location._id ? 0.4 : 0.2,
                  strokeColor: location.color || "#FF0000",
                  strokeOpacity: 1,
                  strokeWeight: selectedLocation?._id === location._id ? 3 : 2,
                  editable: false,
                  draggable: false,
                }}
                onClick={() => showLocationOnMap(location)}
              />
            );
          })}

          {/* Drawing manager */}
          <DrawingManager
            onPolygonComplete={onPolygonComplete}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google?.maps?.ControlPosition?.TOP_CENTER,
                drawingModes: ["polygon"],
              },
              polygonOptions: {
                editable: true,
                draggable: true,
                fillColor: "#FF0000",
                fillOpacity: 0.3,
                strokeColor: "#FF0000",
                strokeOpacity: 1,
                strokeWeight: 2,
              },
            }}
          />
        </GoogleMap>
      </div>
    </div>
  ) : (
    <div>Loading...</div>
  );
};

export default MapWithPolygons;
